import { useReducer, useEffect, useRef } from 'react';

import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import Layout from '../../Layouts';
import * as T from '../../common/Typography';
import * as S from './style';
import { Row, Col } from '../../common/Grid';
import { BasicInput, Select } from '../../common/Inputs';
import Button from '../../common/Button';
import Icon from '../../common/Icon';
import Modal from '../../common/modal';
import validate from '../../../validation/schemas/addUser';
import { TRAINERS_URL } from '../../../constants/navigationRoutes';
import {
  userRoles,
  organisations as organisationsOptions,
  localAuthoritiesBasic as localAuthoritiesOptions
} from '../../../constants';
import {
  fetchLocalLeads as fetchLocalLeadsAction,
  addUser,
  upgradeParticipantToTrainer
} from '../../../actions/users';

const initialState = {
  email: '',
  name: '',
  submitAttempt: false,
  role: userRoles.trainer,
  localLead: null,
  organisation: { value: '', category: '' },
  organisationOther: '',
  localAuthorities: [],
  showOrgOther: false,
  validationErrors: {},
  participants: [],
  selectedParticipantId: null,
  emailExists: false,
  nameMatch: null,
  isDirty: false,
  showLeaveModal: false,
  pendingLocation: null
};

const stateReducer = (state, newState) => {
  return { ...state, ...newState };
};

const cleanEmail = email => email.toLowerCase().trim();

const AdminAddUser = ({
  loading,
  localLeadsList,
  fetchLocalLeads,
  addUser,
  upgradeParticipantToTrainer
}) => {
  const [state, setState] = useReducer(stateReducer, initialState);
  const history = useHistory();
  const unblockRef = useRef(null);
  const participantSearchTimeout = useRef(null);
  const nameMatchTimeout = useRef(null);
  const selectedParticipantIdRef = useRef(null);
  const {
    name,
    email,
    role,
    localLead,
    organisation,
    organisationOther,
    showOrgOther,
    localAuthorities,
    validationErrors,
    participants,
    selectedParticipantId,
    emailExists,
    nameMatch,
    isDirty,
    showLeaveModal,
    pendingLocation
  } = state;

  useEffect(() => {
    setState({ showOrgOther: organisation.value.includes('specify') });
  }, [organisation.value]);

  useEffect(() => {
    fetchLocalLeads();
  }, [fetchLocalLeads]);

  useEffect(() => {
    selectedParticipantIdRef.current = selectedParticipantId;
  }, [selectedParticipantId]);

  const fetchParticipantOptions = async searchTerm => {
    try {
      const { data } = await axios.get('/api/users/admin/participants/search', {
        params: { searchTerm }
      });
      setState({ participants: data.participantList });
    } catch (error) {
      setState({ participants: [] });
    }
  };

  const handleParticipantSearch = searchTerm => {
    if (participantSearchTimeout.current) {
      clearTimeout(participantSearchTimeout.current);
    }
    participantSearchTimeout.current = setTimeout(
      () => fetchParticipantOptions(searchTerm),
      300
    );
  };

  useEffect(() => {
    fetchParticipantOptions('');
    return () => {
      clearTimeout(participantSearchTimeout.current);
      clearTimeout(nameMatchTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDirty) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return undefined;
    }

    unblockRef.current = history.block(targetLocation => {
      setState({ showLeaveModal: true, pendingLocation: targetLocation });
      return false;
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isDirty, history]);

  const validateForm = () => {
    try {
      validate({
        email: cleanEmail(email),
        name,
        role,
        localAuthorities
      });

      setState({ validationErrors: {} });

      return true;
    } catch (error) {
      if (error.name === 'ValidationError') {
        setState({ validationErrors: error.inner });
      }
      return false;
    }
  };

  useEffect(() => {
    if (state.submitAttempt && !selectedParticipantId) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, email, role, localAuthorities]);

  const selectParticipant = participantId => {
    if (!participantId) {
      setState({
        selectedParticipantId: null,
        name: '',
        email: '',
        validationErrors: {},
        emailExists: false,
        submitAttempt: false
      });
      return;
    }

    const participant = participants.find(item => item._id === participantId);
    if (!participant) return;

    setState({
      selectedParticipantId: participantId,
      name: participant.name,
      email: '',
      validationErrors: {},
      emailExists: false,
      submitAttempt: false,
      nameMatch: null,
      isDirty: true
    });
  };

  const lookupNameMatch = async typedName => {
    const trimmedName = typedName.trim();
    if (!trimmedName) {
      setState({ nameMatch: null });
      return;
    }
    try {
      const { data } = await axios.get('/api/users/admin/participants/search', {
        params: { searchTerm: trimmedName }
      });
      if (selectedParticipantIdRef.current) return;
      const matchedParticipant = data.participantList.find(
        item => item.name.toLowerCase() === trimmedName.toLowerCase()
      );
      setState({ nameMatch: matchedParticipant || null });
    } catch (error) {
      setState({ nameMatch: null });
    }
  };

  const handleNameChange = value => {
    setState({ name: value, isDirty: true });
    if (selectedParticipantId) return;
    if (nameMatchTimeout.current) clearTimeout(nameMatchTimeout.current);
    nameMatchTimeout.current = setTimeout(() => lookupNameMatch(value), 300);
  };

  const checkEmailUniqueness = async () => {
    if (selectedParticipantId || !email.trim()) return;

    try {
      const { data } = await axios.get('/api/users', {
        params: { email: cleanEmail(email) }
      });
      if (selectedParticipantIdRef.current) return;
      setState({ emailExists: !data.isUnique });
    } catch (error) {
      setState({ emailExists: false });
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    const organisationPayload = {
      ...organisation,
      value: showOrgOther ? organisationOther : organisation.value
    };

    if (selectedParticipantId) {
      setState({ isDirty: false });
      upgradeParticipantToTrainer(
        {
          participantUserId: selectedParticipantId,
          role,
          localLead,
          organisation: organisationPayload,
          localAuthorities
        },
        history
      );
      return;
    }

    setState({ submitAttempt: true });

    if (emailExists) return;
    if (!validateForm()) return;

    setState({ isDirty: false });

    addUser(
      {
        name,
        email,
        role,
        localLead,
        organisation: organisationPayload,
        localAuthorities
      },
      history
    );
  };

  const handleConfirmLeave = () => {
    setState({ showLeaveModal: false });

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    if (pendingLocation) {
      history.push(pendingLocation.pathname + pendingLocation.search);
    }
  };

  const emailError =
    validationErrors.email ||
    (emailExists &&
      'This email address already exists, please select it from the current participants list');

  return (
    <Layout>
      <S.BackButtonWrapper>
        <Button
          to={TRAINERS_URL}
          type="tertiary"
          aria-label="Back"
          label={
            <S.BackLabel>
              <S.BackChevron>
                <Icon icon="chevron" color="white" width="0.875rem" />
              </S.BackChevron>
              Back
            </S.BackLabel>
          }
        />
      </S.BackButtonWrapper>
      <T.H1 mb="7">Add new trainer</T.H1>
      <S.Form>
        <Row inner>
          <Col w={[4, 5, 4]}>
            <BasicInput
              label="Email"
              value={email}
              handleChange={value =>
                setState({ email: value, emailExists: false, isDirty: true })
              }
              onBlur={checkEmailUniqueness}
              placeholder="Email"
              required
              disabled={!!selectedParticipantId}
              error={emailError}
              mb={3}
            />
          </Col>
          <Col w={[4, 2, 1]}>
            <S.OrSeparator weight={700}>OR</S.OrSeparator>
          </Col>
          <Col w={[4, 5, 4]}>
            <Select
              label="Select from current participants"
              placeholder="Search email"
              showSearch
              allowClear
              filterOption={false}
              onSearch={handleParticipantSearch}
              selected={selectedParticipantId}
              options={participants.map(participant => ({
                value: participant._id,
                label: `${participant.name} — ${participant.email}`
              }))}
              handleChange={selectParticipant}
            />
          </Col>
        </Row>

        <Row inner>
          <Col w={[4, 5, 4]}>
            <BasicInput
              label="Name"
              value={name}
              handleChange={handleNameChange}
              placeholder="Full name"
              mb={3}
              required
              disabled={!!selectedParticipantId}
              error={validationErrors.name}
            />
            {nameMatch && (
              <S.NameMatchBanner>
                <T.P color="white" m="0">
                  A participant with this name already exists with the email
                  address {nameMatch.email}. Please check you wish to create a
                  new user before continuing.
                </T.P>
              </S.NameMatchBanner>
            )}
            <Select
              placeholder="Select role"
              label="Role"
              selected={state.role}
              onChange={value => setState({ role: value, isDirty: true })}
              required
              options={[
                { value: userRoles.trainer, label: 'Trainer' },
                { value: userRoles.localLead, label: 'Local Lead' }
              ]}
              m={{ mb: '3' }}
            />
            {role === userRoles.trainer && (
              <Select
                label="Local lead"
                placeholder="Optionally select trainer's lead"
                selected={localLead}
                allowClear
                options={localLeadsList.map(lead => ({
                  value: lead._id,
                  label: lead.name
                }))}
                onChange={lead => setState({ localLead: lead, isDirty: true })}
                m={{ mb: '3' }}
              />
            )}
            <Select
              label="Organisation"
              selected={organisation.value}
              placeholder="Where do they work?"
              groupedOptions
              options={organisationsOptions}
              handleChange={(value, option) =>
                setState({
                  organisation: {
                    value,
                    category: option?.key?.split('_')[0]
                  },
                  isDirty: true
                })
              }
              m={{ mb: 4 }}
            />
            {showOrgOther && (
              <BasicInput
                value={organisationOther}
                autoFocus
                handleChange={value =>
                  setState({ organisationOther: value, isDirty: true })
                }
                placeholder="Type organisation here..."
                mb={4}
                error={
                  validationErrors.organisation ||
                  validationErrors.organisationOther
                }
              />
            )}
            <Select
              label="Local authority"
              placeholder="Where do they deliver training"
              options={localAuthoritiesOptions.map(localAuthority => ({
                label: localAuthority,
                value: localAuthority
              }))}
              addNew
              multi
              selected={localAuthorities}
              handleChange={values =>
                setState({ localAuthorities: values, isDirty: true })
              }
              error={validationErrors.localAuthorities}
              m={{ mb: 5 }}
            />
            <Button
              onClick={handleSubmit}
              type="primary"
              label="Add user"
              loading={loading}
            />
          </Col>
        </Row>
      </S.Form>
      <Modal
        type="confirm"
        visible={showLeaveModal}
        setModalOpen={value => setState({ showLeaveModal: value })}
        title="Are you sure you want to proceed?"
        text="The information you have recorded will not be saved."
        cancelText="Back to add user"
        confirmText="Exit page"
        parentFunc={handleConfirmLeave}
      />
    </Layout>
  );
};

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  localLeadsList: state.fetchedData.localLeadsList,
  loading:
    state.loading.addUserLoading || state.loading.upgradeParticipantLoading
});

export default connect(mapStateToProps, {
  addUser,
  upgradeParticipantToTrainer,
  fetchLocalLeads: fetchLocalLeadsAction
})(AdminAddUser);
